import { Component, createRef } from 'preact';
import { createPortal } from 'preact/compat';
import { Check, Dice5, Search, X } from 'lucide-react';
import { DiceSetPreview, DiceSetPreviewCaption } from './cosmeticPackPreview';
import { CONTENT_BG_DEFAULT, GOLD, RADIUS, TEXT_PRIMARY } from '../lib/medievalTheme';
import { DICE_PALETTES, getDicePalette, onDicePaletteChange, setDicePalette } from '../utils/game/dice3d/dieAssets';
import { DICE_SKINS } from '../utils/game/dice3d/diceSkins.data';
import { DICE_SKIN_ENTITLEMENTS } from '../utils/game/dice3d/diceSkinEntitlements';
import { getCosmeticsCached, peekCosmeticsCached } from '../utils/arena/cosmeticsCache';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { playUI, UI } from '../utils/arena/uiSounds';
import './DiceAppearancePicker.css';
import { t } from '../localization/t';

// Shared with the regular dice tray so both surfaces keep the same finish.
export const DICE_PANEL_SURFACE = {
  background: `${CONTENT_BG_DEFAULT}, #0e0a06`,
  backgroundBlendMode: 'overlay, normal',
  border: `1px solid ${GOLD} 0.6)`,
  borderRadius: RADIUS.xl,
  boxShadow: [
    '0 1.125cqw 3cqw rgba(0,0,0,0.65)',
    `0 0 2.5cqw ${GOLD} 0.12)`,
    'inset 0 0 2.25cqw rgba(0,0,0,0.5)',
  ].join(', '),
};

// Unlike the renderer's boot-time fallback, the collection never guesses
// ownership. Keep owned recipes visible even if their shop listing retires.
function ownedDice(catalog) {
  const owned = new Set(catalog?.owned || []);
  return Object.values(DICE_SKINS).filter((skin) => {
    const entitlement = DICE_SKIN_ENTITLEMENTS[skin.id];
    return entitlement?.free || owned.has(entitlement?.cosmeticId);
  }).map((skin) => {
    const pack = catalog?.packs?.find((p) => p.diceSkinId === skin.id);
    return { ...pack, diceSkinId: skin.id, name: pack?.name || skin.label };
  }).sort((a, b) => a.name.localeCompare(b.name));
}

export default class DiceAppearancePicker extends Component {
  state = { palette: getDicePalette().id, open: false };
  trigger = createRef();
  anchor = createRef();

  componentDidMount() {
    this.unsubscribe = onDicePaletteChange((palette) => this.setState({ palette: palette.id }));
  }

  componentWillUnmount() {
    this.unsubscribe?.();
  }

  close = () => {
    this.setState({ open: false });
    this.trigger.current?.focus({ preventScroll: true });
  };

  pick = (id) => {
    if (id !== getDicePalette().id) {
      setDicePalette(id);
      playUI(UI.CLICK);
    }
    if (this.state.open) this.close();
  };

  render() {
    const { palette, open } = this.state;
    const custom = !!DICE_SKINS[palette];
    return (
      <div ref={this.anchor} className="vh-dice-appearance" role="group" aria-label={t('Dice appearance')}>
        {['brown', 'bone', 'stone'].map((id) => (
          <button
            key={id} type="button" className="vh-dice-swatch"
            title={DICE_PALETTES[id].label} aria-label={`${DICE_PALETTES[id].label} dice`}
            aria-pressed={palette === id} onClick={() => this.pick(id)}
          >
            <span style={{ background: DICE_PALETTES[id].swatch }} />
          </button>
        ))}
        <span className="vh-dice-appearance-divider" aria-hidden="true" />
        <button
          ref={this.trigger} type="button" className="vh-dice-collection-trigger"
          aria-label={t('Choose custom dice')} aria-haspopup="dialog" aria-expanded={open}
          data-custom-active={custom || undefined}
          title={custom ? t('Custom dice · {palette}', { palette: t(DICE_PALETTES[palette].label) }) : t('Your custom dice')}
          onClick={() => { playUI(UI.OPEN); this.setState({ open: true }); }}
        >
          <Dice5 size={20} aria-hidden="true" />
          {custom && <span className="vh-dice-custom-dot" />}
        </button>
        {open && createPortal(
          <OwnedDicePanel anchor={this.anchor} side={this.props.side} current={palette} onPick={this.pick} onClose={this.close} />,
          document.body,
        )}
      </div>
    );
  }
}

class OwnedDicePanel extends Component {
  state = { query: '', catalog: peekCosmeticsCached(), loading: true, error: false };
  panel = createRef();
  search = createRef();

  componentDidMount() {
    this.escToken = pushDismiss(this.props.onClose);
    this.position();
    this.panel.current?.focus({ preventScroll: true });
    window.addEventListener('resize', this.position);
    this.load();
  }

  componentWillUnmount() {
    this.unmounted = true;
    removeDismiss(this.escToken);
    window.removeEventListener('resize', this.position);
  }

  load = async () => {
    this.setState({ loading: true, error: false });
    try {
      const catalog = await getCosmeticsCached();
      if (!this.unmounted) this.setState({ catalog, loading: false });
    } catch {
      if (!this.unmounted) this.setState({ loading: false, error: true });
    }
  };

  position = () => {
    const panel = this.panel.current;
    const anchor = this.props.anchor.current;
    if (!panel || !anchor) return;
    const rect = (anchor.closest('[data-dice-appearance-anchor], [data-dice-tray]') || anchor).getBoundingClientRect();
    const { width, height } = panel.getBoundingClientRect();
    const gap = 12;
    const right = rect.right + gap;
    const left = rect.left - width - gap;
    const preferred = this.props.side === 'left' ? left : right;
    const alternate = this.props.side === 'left' ? right : left;
    const fits = (x) => x >= gap && x + width <= window.innerWidth - gap;
    const x = fits(preferred) ? preferred : fits(alternate) ? alternate : preferred;
    panel.style.left = `${Math.max(gap, Math.min(x, window.innerWidth - width - gap))}px`;
    panel.style.top = `${Math.max(gap, Math.min(rect.top, window.innerHeight - height - gap))}px`;
  };

  onKeyDown = (event) => {
    // Keep game shortcuts and roll-card activation out of this dialog.
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      this.props.onClose();
    }
    if (event.key !== 'Tab') return;
    const targets = [...this.panel.current.querySelectorAll('button:not(:disabled), input')];
    const first = targets[0];
    const last = targets[targets.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel.current)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  render() {
    const { query, catalog, loading, error } = this.state;
    const packs = ownedDice(catalog);
    const visible = packs.filter((p) => p.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
    return (
      <div className="vh-dice-collection-layer" onPointerDown={(e) => { if (e.target === e.currentTarget) this.props.onClose(); }} onKeyDown={this.onKeyDown}>
        <section ref={this.panel} className="vh-dice-collection" tabIndex={-1} role="dialog" aria-modal="true" aria-label={t('Your dice')} style={{ ...DICE_PANEL_SURFACE, color: TEXT_PRIMARY }}>
          <header className="vh-dice-collection-header">
            <h2 className="arena-heading">{t('Your dice')}</h2>
            <div className="vh-dice-search">
              <Search size={14} aria-hidden="true" />
              <input ref={this.search} type="search" aria-label={t('Search your dice')} placeholder={t('Search…')} value={query} onInput={(e) => { this.setState({ query: e.currentTarget.value }); this.grid?.scrollTo?.(0, 0); }} />
            </div>
            <button type="button" className="vh-dice-icon-button" aria-label={t('Close dice collection')} title={t('Close')} onClick={this.props.onClose}><X size={18} aria-hidden="true" /></button>
          </header>
          <div ref={(el) => { this.grid = el; }} className="vh-dice-collection-scroll">
            {visible.length > 0 ? (
              <div className="vh-dice-collection-grid">
                {visible.map((pack) => {
                  const active = pack.diceSkinId === this.props.current;
                  return (
                    <button key={pack.diceSkinId} type="button" className={`vh-mode-tile vh-dice-owned-tile${active ? ' vh-mode-tile--selected' : ''}`} aria-label={`Equip ${pack.name} dice`} aria-pressed={active} title={pack.name} onClick={() => this.props.onPick(pack.diceSkinId)}>
                      <DiceSetPreview pack={pack} />
                      {active && <span className="vh-dice-equipped"><Check size={12} aria-hidden="true" /> {t('Equipped')}</span>}
                      <DiceSetPreviewCaption pack={pack} />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="vh-dice-empty" role="status">
                <Dice5 size={32} strokeWidth={1.2} aria-hidden="true" />
                <strong className="arena-heading">{loading && !catalog ? t('Loading your dice…') : error && !catalog ? t('Collection unavailable') : query.trim() ? t('No matching dice') : t('Your collection awaits')}</strong>
                <p>{loading && !catalog ? 'Gathering your collection.' : error && !catalog ? 'Your standard colours are still available.' : query.trim() ? 'Try another name or clear your search.' : 'Dice you purchase in the shop will appear here.'}</p>
                {error && <button type="button" className="vh-dice-text-button" onClick={this.load}>{t('Try again')}</button>}
                {query.trim() && <button type="button" className="vh-dice-text-button" onClick={() => { this.setState({ query: '' }); this.search.current?.focus(); }}>{t('Clear search')}</button>}
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }
}
